import React from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

function muxThumb(playbackId) {
  if (!playbackId) return "";
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?time=5&fit_mode=pad&width=960&height=540`;
}

function wrapIndex(value, length) {
  if (!length) return 0;
  return ((value % length) + length) % length;
}

function channelSlug(channel) {
  return String(channel?.slug || channel?.id || "").trim();
}

function prettySlugLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Channel";
  return raw
    .split("_")
    .filter(Boolean)
    .map((part) => part.toUpperCase())
    .join("_");
}

function channelName(channel) {
  const slug = channelSlug(channel);
  const normalizedSlug = slug.toLowerCase();

  // This channel's backend name can be overwritten by stream/event title data.
  // Keep the channel selector showing the real streamer/channel name.
  if (normalizedSlug === "getactive_pet3") return "GetActive_PET3";

  const label = String(
    channel?.displayName ||
      channel?.streamerName ||
      channel?.name ||
      channel?.title ||
      ""
  ).trim();

  return label || prettySlugLabel(slug || channel?.id);
}

function channelGame(channel) {
  return String(channel?.streamMeta?.game || channel?.meta?.game || channel?.game || "").trim();
}

function channelTitle(channel) {
  return String(channel?.streamMeta?.title || channel?.meta?.title || channel?.title || "").trim();
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function lastLiveText(channel) {
  if (channel?.isLive) return "Live now";
  const date = formatDate(channel?.lastLiveAt || channel?.lastVodCreatedAt || channel?.lastVodDate);
  return date ? `Last live ${date}` : "No sessions yet";
}

function thumbFor(channel) {
  const playerPlayback = channel?.player?.playbackId;
  const livePlayback = channel?.livePlaybackId || channel?.playbackId;
  const vodPlayback = channel?.lastVodPlaybackId;
  return muxThumb(channel?.isLive ? livePlayback || playerPlayback : vodPlayback || playerPlayback);
}

export default function Streams() {
  const navigate = useNavigate();
  const [channels, setChannels] = React.useState([]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const dragStartRef = React.useRef(null);

  React.useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const r = await api.streams();
        if (!alive) return;
        if (r.ok) {
          const next = Array.isArray(r.data?.channels) ? r.data.channels : [];
          setChannels(next);
          setActiveIndex((current) => wrapIndex(current, next.length));
          setErr(null);
        } else {
          setErr(r.status || "load_failed");
        }
      } catch (e) {
        if (alive) setErr("load_failed");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const active = channels[activeIndex] || null;
  const count = Math.max(1, channels.length);
  const radius = count <= 3 ? 280 : count <= 5 ? 350 : 410;

  function rotateBy(amount) {
    setActiveIndex((current) => wrapIndex(current + amount, channels.length));
  }

  function openChannel(channel = active) {
    const slug = channelSlug(channel);
    if (slug) navigate(`/stream/${encodeURIComponent(slug)}`);
  }

  function pointerX(evt) {
    return evt?.touches?.[0]?.clientX ?? evt?.changedTouches?.[0]?.clientX ?? evt?.clientX ?? 0;
  }

  function onPointerDown(evt) {
    dragStartRef.current = pointerX(evt);
  }

  function onPointerUp(evt) {
    const start = dragStartRef.current;
    dragStartRef.current = null;
    if (start == null) return;
    const diff = pointerX(evt) - start;
    if (Math.abs(diff) < 45) return;
    rotateBy(diff > 0 ? -1 : 1);
  }

  React.useEffect(() => {
    function onKey(evt) {
      if (evt.key === "ArrowLeft") rotateBy(-1);
      if (evt.key === "ArrowRight") rotateBy(1);
      if (evt.key === "Enter") openChannel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels.length, activeIndex]);

  return (
    <main className="page streams3dPage">
      <section className="streams3dHead">
        <div>
          <h1>Channels</h1>
        </div>

        <div className="streams3dControls">
          <button type="button" onClick={() => rotateBy(-1)} disabled={!channels.length} aria-label="Previous channel">
            ◀
          </button>
          <span>{channels.length ? activeIndex + 1 : 0}/{channels.length}</span>
          <button type="button" onClick={() => rotateBy(1)} disabled={!channels.length} aria-label="Next channel">
            ▶
          </button>
        </div>
      </section>

      {err && <div className="streams3dNotice">Failed to load channels.</div>}
      {loading && <div className="streams3dNotice">Loading channels...</div>}

      {!loading && channels.length === 0 && (
        <div className="streams3dNotice">No channels configured yet.</div>
      )}

      {channels.length > 0 && (
        <>
          <section
            className="streams3dStage"
            onMouseDown={onPointerDown}
            onMouseUp={onPointerUp}
            onTouchStart={onPointerDown}
            onTouchEnd={onPointerUp}
          >
            <div className="streams3dOrbit streams3dOrbitA" />
            <div className="streams3dOrbit streams3dOrbitB" />
            <div className="streams3dCore">GA</div>

            <div className="streams3dRing" style={{ "--ring-radius": `${radius}px` }}>
              {channels.map((channel, index) => {
                const offset = index - activeIndex;
                const angle = (360 / count) * offset;
                const absRaw = Math.abs(offset);
                const distance = Math.min(absRaw, count - absRaw);
                const selected = index === activeIndex;
                const img = thumbFor(channel);

                return (
                  <button
                    type="button"
                    key={channel.id || channel.slug || index}
                    className={`streams3dCard ${selected ? "selected" : ""} ${channel.isLive ? "live" : "idle"}`}
                    style={{
                      transform: `translate(-50%, -50%) rotateY(${angle}deg) translateZ(var(--ring-radius)) rotateY(${-angle}deg)`,
                      opacity: selected ? 1 : distance > 2 ? 0.22 : 0.58,
                      zIndex: selected ? 20 : 20 - distance,
                    }}
                    onClick={() => (selected ? openChannel(channel) : setActiveIndex(index))}
                  >
                    <span className="streams3dBadge">{channel.isLive ? "LIVE" : "IDLE"}</span>
                    <span className="streams3dName">{channelName(channel)}</span>
                    <span className="streams3dDate">{lastLiveText(channel)}</span>
                    <span className="streams3dThumb">
                      {img ? <img src={img} alt="" /> : <em>No video yet</em>}
                      <i>{channel.isLive ? "Live now" : channel.lastVodPlaybackId ? "Latest VOD" : "No video"}</i>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="streams3dSelected">
            <div>
              <span className={`streams3dPill ${active?.isLive ? "live" : "idle"}`}>{active?.isLive ? "LIVE" : "IDLE"}</span>
              <h2>{channelName(active)}</h2>
              <p>{channelTitle(active) || channelGame(active) || `Channel: ${channelSlug(active)}`}</p>
              <small>{lastLiveText(active)}</small>
            </div>
            <button type="button" onClick={() => openChannel(active)}>Open Stream</button>
          </section>
        </>
      )}

      <style>{`
        .streams3dPage{
          min-height:calc(100vh - 90px);
          padding:24px 18px 46px;
          color:#fff;
          overflow:hidden;
        }

        .streams3dHead{
          width:min(1180px, 96vw);
          margin:0 auto 8px;
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:18px;
          position:relative;
          z-index:3;
        }


        .streams3dHead h1{
          margin:0;
          font-family:Georgia, "Times New Roman", serif;
          font-size:clamp(40px, 6vw, 82px);
          line-height:.9;
          text-transform:uppercase;
          text-shadow:0 0 22px rgba(255,0,45,.36), 0 8px 28px rgba(0,0,0,.9);
        }


        .streams3dControls{
          display:flex;
          align-items:center;
          gap:10px;
          padding:10px;
          border-radius:999px;
          background:rgba(0,0,0,.42);
          border:1px solid rgba(255,255,255,.14);
          box-shadow:0 18px 50px rgba(0,0,0,.42);
          backdrop-filter:blur(10px);
        }

        .streams3dControls button{
          width:42px;
          height:42px;
          border-radius:999px;
          border:1px solid rgba(255,255,255,.18);
          background:linear-gradient(180deg, rgba(255,255,255,.13), rgba(255,255,255,.04));
          color:#fff;
          font-weight:950;
          cursor:pointer;
        }

        .streams3dControls button:hover{ background:rgba(170,0,24,.88); }
        .streams3dControls button:disabled{ opacity:.45; cursor:not-allowed; }
        .streams3dControls span{ min-width:58px; text-align:center; font-weight:950; letter-spacing:.1em; }

        .streams3dNotice{
          width:min(760px, 94vw);
          margin:28px auto;
          padding:20px;
          border-radius:22px;
          text-align:center;
          background:rgba(0,0,0,.48);
          border:1px solid rgba(255,255,255,.12);
        }

        .streams3dStage{
          width:min(1180px, 96vw);
          height:450px;
          margin:-4px auto 10px;
          position:relative;
          perspective:1200px;
          transform-style:preserve-3d;
          cursor:grab;
          user-select:none;
        }

        .streams3dStage:active{ cursor:grabbing; }

        .streams3dStage:before{
          content:"";
          position:absolute;
          left:50%;
          top:52%;
          width:min(720px, 76vw);
          height:290px;
          transform:translate(-50%, -50%);
          border-radius:999px;
          background:radial-gradient(circle, rgba(211,0,39,.28), rgba(0,198,255,.12) 42%, transparent 72%);
          filter:blur(16px);
          pointer-events:none;
        }

        .streams3dRing{
          position:absolute;
          inset:0;
          transform-style:preserve-3d;
        }

        .streams3dCard{
          position:absolute;
          left:50%;
          top:50%;
          width:246px;
          min-height:270px;
          padding:13px;
          text-align:left;
          color:#fff;
          border:1px solid rgba(255,255,255,.16);
          border-radius:23px;
          background:linear-gradient(180deg, rgba(21,25,40,.92), rgba(0,0,0,.76));
          box-shadow:0 26px 70px rgba(0,0,0,.72), inset 0 0 28px rgba(255,255,255,.045);
          cursor:pointer;
          overflow:hidden;
          transform-style:preserve-3d;
          transition:transform .5s cubic-bezier(.2,.8,.2,1), opacity .35s, filter .35s, box-shadow .35s;
        }

        .streams3dCard:before{
          content:"";
          position:absolute;
          inset:-70px -40px auto;
          height:160px;
          background:radial-gradient(circle, rgba(255,0,45,.34), transparent 65%);
          transform:rotate(-10deg);
        }

        .streams3dCard.selected{
          border-color:rgba(255,255,255,.42);
          box-shadow:0 34px 88px rgba(0,0,0,.84), 0 0 34px rgba(255,0,50,.36), inset 0 0 28px rgba(255,255,255,.08);
        }

        .streams3dCard:not(.selected){ filter:saturate(.7) brightness(.72); }

        .streams3dCard.live{
          box-shadow:0 30px 78px rgba(0,0,0,.75), 0 0 30px rgba(255,0,34,.38);
        }

        .streams3dBadge{
          position:absolute;
          top:12px;
          right:12px;
          z-index:2;
          padding:4px 10px;
          border-radius:999px;
          background:rgba(255,255,255,.15);
          font-size:10px;
          font-weight:950;
          letter-spacing:.12em;
        }

        .streams3dCard.live .streams3dBadge{
          background:linear-gradient(90deg,#f0002f,#ffbd2d);
          box-shadow:0 0 20px rgba(255,0,45,.45);
        }

        .streams3dName{
          position:relative;
          z-index:1;
          display:block;
          padding-right:62px;
          font-family:Georgia, "Times New Roman", serif;
          font-size:22px;
          line-height:1.05;
          font-weight:950;
          text-transform:uppercase;
          text-shadow:0 4px 14px rgba(0,0,0,.9);
        }

        .streams3dDate{
          position:relative;
          z-index:1;
          display:block;
          margin-top:5px;
          color:rgba(255,255,255,.72);
          text-transform:uppercase;
          letter-spacing:.08em;
          font-size:11px;
        }

        .streams3dThumb{
          position:relative;
          z-index:1;
          display:flex;
          align-items:center;
          justify-content:center;
          margin-top:14px;
          height:145px;
          border-radius:17px;
          overflow:hidden;
          background:rgba(0,0,0,.46);
          border:1px solid rgba(255,255,255,.1);
        }

        .streams3dThumb img{ width:100%; height:100%; object-fit:cover; display:block; }
        .streams3dThumb em{ font-style:normal; color:rgba(255,255,255,.72); text-transform:uppercase; letter-spacing:.12em; }

        .streams3dThumb i{
          position:absolute;
          left:12px;
          bottom:12px;
          padding:6px 10px;
          border-radius:999px;
          background:rgba(0,0,0,.62);
          color:#fff;
          font-size:10px;
          font-style:normal;
          font-weight:950;
          letter-spacing:.1em;
          text-transform:uppercase;
        }

        .streams3dOrbit{
          position:absolute;
          left:50%;
          top:52%;
          transform:translate(-50%, -50%) rotateX(68deg);
          border-radius:50%;
          border:1px solid rgba(255,255,255,.13);
          box-shadow:0 0 28px rgba(255,0,45,.16), inset 0 0 28px rgba(0,198,255,.08);
          pointer-events:none;
        }

        .streams3dOrbitA{ width:min(760px, 78vw); height:250px; }
        .streams3dOrbitB{ width:min(590px, 61vw); height:190px; border-color:rgba(255,0,45,.2); }

        .streams3dCore{
          position:absolute;
          left:50%;
          top:52%;
          width:130px;
          height:130px;
          transform:translate(-50%, -50%);
          display:grid;
          place-items:center;
          border-radius:50%;
          color:rgba(255,255,255,.85);
          font-family:Georgia, "Times New Roman", serif;
          font-size:44px;
          font-weight:950;
          background:radial-gradient(circle, rgba(255,0,45,.2), rgba(0,0,0,.2) 55%, transparent 75%);
          text-shadow:0 0 22px rgba(255,0,45,.5);
          pointer-events:none;
        }

        .streams3dSelected{
          width:min(760px, 90vw);
          margin:0 auto;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:18px;
          padding:18px 20px;
          border-radius:23px;
          background:linear-gradient(180deg, rgba(255,255,255,.12), rgba(255,255,255,.045));
          border:1px solid rgba(255,255,255,.14);
          box-shadow:0 18px 60px rgba(0,0,0,.45);
          backdrop-filter:blur(12px);
        }

        .streams3dPill{
          display:inline-flex;
          padding:5px 12px;
          border-radius:999px;
          background:rgba(255,255,255,.15);
          font-size:12px;
          font-weight:950;
          letter-spacing:.14em;
        }

        .streams3dPill.live{
          background:linear-gradient(90deg,#f0002f,#ffbd2d);
          box-shadow:0 0 22px rgba(255,0,45,.45);
        }

        .streams3dSelected h2{
          margin:8px 0 0;
          font-family:Georgia, "Times New Roman", serif;
          font-size:clamp(22px, 3vw, 34px);
          text-transform:uppercase;
        }

        .streams3dSelected p{ margin:4px 0 0; color:rgba(255,255,255,.78); font-weight:800; }
        .streams3dSelected small{ display:block; margin-top:4px; color:rgba(255,255,255,.58); letter-spacing:.08em; text-transform:uppercase; }

        .streams3dSelected button{
          min-width:185px;
          padding:13px 18px;
          border:0;
          border-radius:999px;
          background:linear-gradient(90deg,#00c6ff,#ff48c4);
          color:#fff;
          font-weight:950;
          text-transform:uppercase;
          letter-spacing:.12em;
          cursor:pointer;
          box-shadow:0 18px 42px rgba(0,198,255,.2), 0 18px 42px rgba(255,72,196,.18);
        }

        @media (max-width: 900px){
          .streams3dHead{ flex-direction:column; }
          .streams3dStage{ height:400px; perspective:900px; }
          .streams3dRing{ --ring-radius:270px !important; }
          .streams3dCard{ width:226px; min-height:256px; }
          .streams3dSelected{ flex-direction:column; align-items:stretch; }
          .streams3dSelected button{ width:100%; }
        }

        @media (max-width: 640px){
          .streams3dPage{ padding:14px 10px 34px; }
          .streams3dStage{ height:360px; }
          .streams3dRing{ --ring-radius:210px !important; }
          .streams3dCard{ width:196px; min-height:240px; padding:12px; border-radius:20px; }
          .streams3dName{ font-size:18px; }
          .streams3dDate{ font-size:11px; }
          .streams3dThumb{ height:126px; border-radius:15px; }
          .streams3dOrbitA{ width:86vw; height:200px; }
          .streams3dOrbitB{ width:68vw; height:160px; }
        }
      `}</style>
    </main>
  );
}
